import { jWebReady, ReservedValueStoreKeys } from "@jarvis/jweb-core";
import { isNative, writeDataToValueStore } from "../JWeb";
import {
  OPT_IN_GROUP_VALUE,
  OPT_OUT_GROUP_VALUE,
} from "../AnalyticsService/AnalyticsService";
import {
  EventHandlerType,
  getOptanonConsentObjectValues,
  registerOptanonDataLayerEventHandler,
} from "./optanonUtils";
import bindAllMethods from "../../utils/bindAllMethods";
import { JWebType } from "../JWeb/types";

export type ConsentServiceInputType = {
  handleConsentChange?: () => void;
};

/***
 *  The Consent Service is only used in Web envs.
 */
export default class ConsentService {
  private lastOptInGroups = undefined;
  private _handleConsentChange!: () => void;

  constructor(consentServiceProps: ConsentServiceInputType) {
    const { handleConsentChange } = consentServiceProps;
    if (handleConsentChange) {
      this._handleConsentChange = handleConsentChange;
    }
    bindAllMethods(this);
  }

  public async init(): Promise<void> {
    this._registerOnDataLayer();

    // Initialize lastOptInGroups
    this.lastOptInGroups = (window as any)._O?.getOptInGroups();
  }

  /*** This method is used just to keep up-to-date the consent in value store. */
  public async updateOptInGroupsInValueStore(): Promise<void> {
    const currentOptInGroups = (window as any)._O?.getOptInGroups();
    if (this.lastOptInGroups != currentOptInGroups) {
      await this._writeOptanonConsentInValueStore();
      this.lastOptInGroups = currentOptInGroups;
    }
  }

  private async _writeOptanonConsentInValueStore(): Promise<void> {
    if (await isNative()) {
      return;
    }

    const { optanonConsentId, optanonConsent } =
      getOptanonConsentObjectValues();

    await writeDataToValueStore([
      {
        key: ReservedValueStoreKeys.visitorUuid,
        value: optanonConsentId,
      },
      {
        key: ReservedValueStoreKeys.webApplicationConsent,
        value: optanonConsent,
      },
      {
        key: ReservedValueStoreKeys.applicationInstanceId,
        value: optanonConsentId,
      },
    ]);
  }

  public handleOnOptinGroupsChange(): void {
    this._writeOptanonConsentInValueStore();
    if (this._handleConsentChange) this._handleConsentChange();
  }

  private _registerOnDataLayer(): void {
    const writeOptanonConsentInValueStoreOnOptIn: EventHandlerType = {
      event: "pb.onOptInGroups",
      callback: this.handleOnOptinGroupsChange,
      groups: OPT_IN_GROUP_VALUE,
    };

    const writeOptanonConsentInValueStoreOnOptOut: EventHandlerType = {
      event: "pb.onOptInGroups",
      callback: this.handleOnOptinGroupsChange,
      groups: OPT_OUT_GROUP_VALUE,
    };

    registerOptanonDataLayerEventHandler(
      writeOptanonConsentInValueStoreOnOptIn
    );
    registerOptanonDataLayerEventHandler(
      writeOptanonConsentInValueStoreOnOptOut
    );
  }
}
